import { DfpColumn } from '@models/dfp/dfp-column.model';
import {
  DfpSourceExclusionReason,
  DfpStatementKind,
  dfpSourceDocumentResponse,
  dfpSourceDocumentsResponse,
} from '@interfaces/dfp/response/dfpSourceDocumentsResponse';

/** Anos ocultos e colunas de período pedidas de DRE e Balanço, as duas tabelas que têm documento de origem. */
export interface DfpHiddenYearsContext {
  incomeHiddenYears: readonly number[];
  balanceHiddenYears: readonly number[];
  incomePeriodColumns: readonly DfpColumn[];
  balancePeriodColumns: readonly DfpColumn[];
}

/**
 * Reclassifica como "não utilizado" o documento cuja cobertura só toca anos que o usuário ocultou nesta sessão. O
 * backend não conhece `hiddenYears` (é estado de tela, por tabela), então o ajuste é aqui — senão o painel diria que
 * o arquivo alimenta uma coluna que não está mais visível. (Extraído do `DfpComponent`, card 3515, sem mudar a regra.)
 *
 * Cuidado: ocultar um ano NÃO oculta as colunas de PERÍODO daquele ano — `hiddenYears` só derruba a coluna anual
 * consolidada (ver `TableAbstractComponent.buildVisibleColumns`). É exatamente o fluxo do "Substituir 2024 por um
 * período": o ano some, mas a coluna `2024 · jan–jun` entra na tela. Por isso uma cobertura também conta como VISÍVEL
 * quando casa com uma coluna de período pedida para a tabela daquele demonstrativo — senão o documento que alimenta a
 * coluna recém-criada seria demovido para "Não utilizados" com "Período não exibido nas colunas atuais" enquanto a
 * coluna dele está na tela, mostrando os números.
 */
export function applyHiddenYearsToSourceDocuments(
  documents: dfpSourceDocumentsResponse | null,
  context: DfpHiddenYearsContext
): dfpSourceDocumentsResponse | null {
  if (!documents) return null;

  const hiddenFor = (kind: DfpStatementKind): readonly number[] => {
    if (kind === DfpStatementKind.IncomeStatement) return context.incomeHiddenYears;
    if (kind === DfpStatementKind.BalanceSheet) return context.balanceHiddenYears;
    return [];
  };

  const periodColumnsForKind = (kind: DfpStatementKind): readonly DfpColumn[] => {
    if (kind === DfpStatementKind.IncomeStatement) return context.incomePeriodColumns;
    if (kind === DfpStatementKind.BalanceSheet) return context.balancePeriodColumns;
    return [];
  };

  const stillVisible = (document: dfpSourceDocumentResponse): boolean => {
    const hidden = hiddenFor(document.kind);
    if (!hidden.length) return true;

    const periodColumns = periodColumnsForKind(document.kind);

    return document.coverage.some(item => {
      if (!hidden.includes(item.year)) return true;

      // Par exato de meses, a mesma identidade que `StatementScopeFilter.Matches` exige no backend: ano inteiro
      // (null/null) nunca casa com um período parcial.
      return periodColumns.some(
        column =>
          column.year === item.year &&
          item.periodStartMonth === column.startMonth &&
          item.periodEndMonth === column.endMonth
      );
    });
  };

  const hiddenOnes = documents.inUse.filter(document => !stillVisible(document));

  // `notUsed === null` = usuário sem acesso a esse grupo (só ADMIN/TENANT veem; o corte é do backend). Nesse caso o
  // documento demovido apenas SAI de "em uso" e não é acrescentado a lugar nenhum: promovê-lo para uma lista
  // invisível não teria efeito, e materializar a lista aqui (trocando `null` por array) faria o painel voltar a
  // exibir a coluna restrita — reintroduzindo pelo cliente o que o servidor cortou.
  return {
    inUse: documents.inUse.filter(stillVisible),
    notUsed:
      documents.notUsed === null
        ? null
        : [
            ...hiddenOnes.map(document => ({
              ...document,
              reason: DfpSourceExclusionReason.PeriodNotDisplayed,
            })),
            ...documents.notUsed,
          ],
  };
}
